import type { CityYieldChartData } from '../../content/landingPages/types'
import { formatKwh } from '../../utils/format'

/** Fixed non-leap-year reference date, only used to resolve the German month abbreviation - same
 *  trick as MonthlyEnergyChart.tsx. DE-only (no i18n) since every page this renders on is itself
 *  DE-only (SEO-4.0-Story Task 08, city pages target only the German market - see the task doc's
 *  "DE-only" decision). */
function monthLabel(month: number): string {
  return new Intl.DateTimeFormat('de-DE', { month: 'short' }).format(new Date(2026, month - 1, 1))
}

function formatSignedPercent(value: number): string {
  const rounded = Math.round(value * 10) / 10
  const sign = rounded > 0 ? '+' : ''
  return `${sign}${rounded.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
}

/**
 * 12-Monats-Ertragsdiagramm für eine Task-08-Städteseite (siehe
 * docs/tasks/active/seo/seo-4.0/08-staedte-rollout-pilot.md) - reines Div/Flex-Balkendiagramm, kein
 * Chart-Framework, gleiches Muster wie components/kunden/MonthlyEnergyChart.tsx (dort:
 * Verbrauchsaufteilung eines Kundenauftrags; hier: ein einzelner PV-Ertragswert pro Monat für
 * eine Stadt, aus `content/landingPages/programmatic/cityYieldFacts.ts`).
 *
 * Zeigt zusätzlich drei Kennzahlen oberhalb des Diagramms (Jahresertrag, Rang, Abweichung vom
 * Bundesdurchschnitt) - die eigentliche Differenzierung der 250 Städteseiten, nicht nur das
 * Diagramm selbst.
 */
export function CityYieldChart({ data }: { data: CityYieldChartData }) {
  const monthlyValuesKwh = data.monthlyShare.map((share) => data.annualYieldKwhPerKwp * share)
  const maxValueKwh = Math.max(...monthlyValuesKwh, 1)
  const deltaPercent =
    ((data.annualYieldKwhPerKwp - data.nationalAverageKwhPerKwp) / data.nationalAverageKwhPerKwp) * 100
  const ariaLabel =
    `Monatlicher PV-Ertrag pro kWp installierter Leistung in ${data.cityName}, ` +
    `insgesamt ${formatKwh(data.annualYieldKwhPerKwp)} im Jahr`

  return (
    <div className="city-yield-chart">
      <div className="city-yield-chart-stats">
        <div className="city-yield-chart-stat">
          <span className="city-yield-chart-stat-value">{formatKwh(data.annualYieldKwhPerKwp)}</span>
          <span className="city-yield-chart-stat-label">Ertrag pro kWp und Jahr (Richtwert)</span>
        </div>
        <div className="city-yield-chart-stat">
          <span className="city-yield-chart-stat-value">
            Platz {data.rank} von {data.totalRankedCities}
          </span>
          <span className="city-yield-chart-stat-label">im Städte-Ranking</span>
        </div>
        <div className="city-yield-chart-stat">
          <span className="city-yield-chart-stat-value">{formatSignedPercent(deltaPercent)}</span>
          <span className="city-yield-chart-stat-label">gegenüber dem Bundesdurchschnitt</span>
        </div>
      </div>

      <div className="city-yield-chart-bars" role="img" aria-label={ariaLabel}>
        {monthlyValuesKwh.map((valueKwh, i) => {
          const month = i + 1
          return (
            <div className="city-yield-chart-column" key={month}>
              <span className="city-yield-chart-value">{Math.round(valueKwh)}</span>
              <div
                className="city-yield-chart-bar"
                style={{ height: `${(valueKwh / maxValueKwh) * 100}%` }}
                title={`${monthLabel(month)}: ${formatKwh(valueKwh)}`}
              />
              <span className="city-yield-chart-month-label">{monthLabel(month)}</span>
            </div>
          )
        })}
      </div>

      <table className="sr-only">
        <caption>{ariaLabel}</caption>
        <thead>
          <tr>
            <th>Monat</th>
            <th>Ertrag pro kWp</th>
          </tr>
        </thead>
        <tbody>
          {monthlyValuesKwh.map((valueKwh, i) => (
            <tr key={i}>
              <td>{monthLabel(i + 1)}</td>
              <td>{formatKwh(valueKwh)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
